export const HERO = {
  headline: 'Expert help for Family Tree Maker',
  text: 'Installation, upgrades, sync, TreeVault, and migration support — live chat guidance when you need it.',
  bullets: [
    'Download & install walkthroughs',
    'Upgrade and computer migration help',
    'TreeVault backup and restore guidance',
    'Sync errors and FamilySearch connection fixes',
  ],
};

export const FEATURES = [
  {
    title: 'Install & Setup',
    text: 'Step-by-step guidance for first-time installs on Windows and Mac, including activation and registration.',
  },
  {
    title: 'Sync & TreeVault',
    text: 'Help resolving sync conflicts, offline trees, and TreeVault backup or restore questions.',
  },
  {
    title: 'Migration Support',
    text: 'Move your family tree to a new computer without losing media, sources, or linked records.',
  },
  {
    title: 'Live Chat Help',
    text: 'Talk to a support agent in real time for quick answers and hands-on troubleshooting.',
  },
];

export const STEPS = [
  {
    step: '1',
    title: 'Tell us your issue',
    text: 'Open live chat and describe what you need — install, sync, migration, or something else.',
  },
  {
    step: '2',
    title: 'Get clear guidance',
    text: 'Our team walks you through the fix with plain-language steps tailored to your version and device.',
  },
  {
    step: '3',
    title: 'Confirm it works',
    text: 'We stay with you until the software is running correctly and your tree is safe.',
  },
];

export const FAQ = [
  {
    q: 'Are you affiliated with Family Tree Maker?',
    a: 'No. We are an independent support provider operated by Nodex Global LLC. We are not affiliated with, endorsed by, or sponsored by Family Tree Maker or its trademark owners.',
  },
  {
    q: 'What versions do you support?',
    a: 'We help with recent Family Tree Maker releases on Windows and macOS, including install, upgrade, sync, and TreeVault questions.',
  },
  {
    q: 'How do I get help quickly?',
    a: 'Live chat is the fastest option — click Chat Now anywhere on this site. We are available 24/7.',
  },
  {
    q: 'Can you help move my tree to a new computer?',
    a: 'Yes. We guide you through backup, transfer, and restore so your tree, media, and sources stay intact.',
  },
];

export const SUPPORT_TOPICS = [
  'Software won’t install or open',
  'Sync errors between devices',
  'TreeVault backup or restore',
  'FamilySearch connection issues',
  'Missing photos or media links',
  'Upgrade from an older version',
];

export const STATS = [
  { value: '24/7', label: 'Live chat support' },
  { value: '10k+', label: 'Trees guided to safety' },
  { value: 'Win + Mac', label: 'Both platforms covered' },
  { value: 'TreeVault', label: 'Backup & sync specialists' },
];

export const ABOUT_PREVIEW = {
  title: 'Independent help for Family Tree Maker users',
  text: 'We are a dedicated help desk for genealogists who need practical guidance — not generic scripts. Whether you are installing for the first time or moving a decades-old tree to a new computer, our team explains each step in plain language.',
  points: [
    'Real-time live chat support',
    'Install, upgrade, and migration guidance',
    'TreeVault, sync, and media troubleshooting',
  ],
};

export const WHY_US = [
  {
    title: 'Genealogy-focused support',
    text: 'We specialize in Family Tree Maker — installs, sync, TreeVault, FamilySearch links, and media paths.',
  },
  {
    title: 'Plain-language guidance',
    text: 'No jargon. We explain what to click, what to back up, and what to expect at each step.',
  },
  {
    title: 'Windows & Mac coverage',
    text: 'Whether you use a desktop PC or a MacBook, we tailor steps to your version and operating system.',
  },
  {
    title: 'Stay until it works',
    text: 'We do not leave mid-fix. Our goal is a working tree — backed up, synced, and ready to continue.',
  },
];

export const GETTING_STARTED = [
  {
    title: 'Back up before big changes',
    text: 'Create a fresh backup before any upgrade or computer move so your tree is always recoverable.',
  },
  {
    title: 'Know your setup',
    text: 'Your FTM version plus Windows or Mac helps us give you the right steps the first time.',
  },
  {
    title: 'Keep media folders handy',
    text: 'If photos link from local folders, leave those folders in place during restore.',
  },
  {
    title: 'Reach out early',
    text: 'Stuck halfway through an install? Chat before trying random fixes that can make things worse.',
  },
];

export const SYSTEM_REQUIREMENTS = {
  windows: [
    { label: 'Operating System', value: 'Windows 10 or later (64-bit)' },
    { label: 'Processor', value: '1 GHz Intel processor or greater' },
    { label: 'RAM', value: '2 GB RAM (4 GB recommended)' },
    { label: 'Hard Disk', value: '1 GB free disk space' },
    { label: 'Display', value: '1024 × 768 or higher' },
    { label: 'Internet', value: 'Required for activation and updates' },
  ],
  mac: [
    { label: 'Operating System', value: 'macOS 10.15 or later' },
    { label: 'Processor', value: 'Intel Core 2 Duo or Apple Silicon' },
    { label: 'RAM', value: '2 GB RAM (4 GB recommended)' },
    { label: 'Hard Disk', value: '1 GB free disk space' },
    { label: 'Display', value: '1024 × 768 or higher' },
    { label: 'Internet', value: 'Required for activation and updates' },
  ],
};

export const TESTIMONIALS = [
  {
    quote:
      'I was ready to give up on my tree after a failed upgrade. One chat session and they had me back within the hour — backup restored and sync working again.',
    name: 'Margaret L.',
    location: 'Portland, OR',
  },
  {
    quote:
      'Moving FTM to a new laptop felt overwhelming until support walked me through backup and restore. Every photo and note came across perfectly.',
    name: 'David R.',
    location: 'Nashville, TN',
  },
  {
    quote:
      'TreeVault kept failing to sync. Chat support pinpointed the issue in minutes and gave me steps I could actually follow on my Mac.',
    name: 'Susan W.',
    location: 'Denver, CO',
  },
];
